import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  Plus, Trash2, Edit2, GripVertical, ChevronDown, ChevronUp, 
  Type, List, CheckSquare, FileText, Upload, ToggleLeft, Info,
  Sparkles, MessageSquare, Palette, Music, Utensils, Accessibility
} from "lucide-react";

/**
 * IntakeFormBuilder - Admin component for building intake forms
 * 
 * STRUCTURE:
 * 1. INTAKE QUESTIONS - Admin can fully edit/add/remove (hosts can only toggle)
 *    - Standard fields: Phone, Company, Title, City, Interests
 *    - Custom questions added by admin
 * 
 * 2. DEFAULTS, INSPO & EXPERIENCE - Separate section below
 *    - Dietary restrictions
 *    - Accessibility needs
 *    - Music preferences
 *    - Style inspiration
 */

const FIELD_TYPES = [
  { id: "text", label: "Short Text", icon: Type, description: "Single line text input" },
  { id: "textarea", label: "Long Text", icon: FileText, description: "Multi-line text area" },
  { id: "select", label: "Single Select", icon: List, description: "Dropdown with one choice" },
  { id: "multiselect", label: "Multi Select", icon: CheckSquare, description: "Multiple choice selection" },
  { id: "file", label: "File Upload", icon: Upload, description: "Allow file attachments" },
  { id: "yesno", label: "Yes/No", icon: Info, description: "Simple yes or no question" },
  { id: "toggle", label: "Toggle", icon: ToggleLeft, description: "On/off switch" },
];

export default function IntakeFormBuilder({ 
  intakeQuestions = [],
  defaultsInspo = {},
  onIntakeQuestionsChange, 
  onDefaultsInspoChange,
  pilotCategory = "instructor"
}) {
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [editingStandardField, setEditingStandardField] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const [newField, setNewField] = useState({
    label: "",
    type: "text",
    required: false,
    placeholder: "",
    options: [],
    helpText: ""
  });

  // Toggle section expansion
  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  // Get current intake questions - use passed questions if available, otherwise use basic defaults
  const getIntakeQuestions = () => {
    // If we have sections from intake_templates.py, use those directly
    if (intakeQuestions.length > 0 && intakeQuestions[0]?.questions) {
      // This is the full template with sections
      return intakeQuestions;
    }
    
    // If we have flat questions, return those
    if (intakeQuestions.length > 0 && intakeQuestions[0]?.id) {
      return intakeQuestions;
    }
    
    // Fallback to basic defaults if no questions passed
    const defaultQuestions = [
      { id: "phone", label: "Phone Number", type: "text", required: false, enabled: true, isStandard: true },
      { id: "company", label: "Company", type: "text", required: false, enabled: true, isStandard: true },
      { id: "title", label: "Job Title", type: "text", required: false, enabled: true, isStandard: true },
      { id: "city", label: "City", type: "text", required: false, enabled: true, isStandard: true },
      { id: "interests", label: "Interests", type: "multiselect", required: false, enabled: true, isStandard: true },
    ];
    return defaultQuestions;
  };
  
  const questions = getIntakeQuestions();
  const hasSections = questions.length > 0 && questions[0]?.questions;

  // Add new custom question
  const handleAddQuestion = () => {
    if (!newField.label.trim()) return;
    
    const question = {
      ...newField,
      id: `custom_${Date.now()}`,
      isStandard: false,
      enabled: true,
      order: intakeQuestions.length
    };
    
    onIntakeQuestionsChange([...intakeQuestions, question]);
    setNewField({ label: "", type: "text", required: false, placeholder: "", options: [], helpText: "" });
    setShowAddDialog(false);
  };

  // Update a question (standard or custom)
  const handleUpdateQuestion = (questionId, updates) => {
    const existing = intakeQuestions.find(q => q.id === questionId);
    if (existing) {
      onIntakeQuestionsChange(intakeQuestions.map(q => 
        q.id === questionId ? { ...q, ...updates } : q
      ));
    } else {
      // Add standard field override
      onIntakeQuestionsChange([...intakeQuestions, { id: questionId, ...updates }]);
    }
  };

  // Delete custom question
  const handleDeleteQuestion = (questionId) => {
    onIntakeQuestionsChange(intakeQuestions.filter(q => q.id !== questionId));
  };

  // Update defaults/inspo field
  const updateDefaultsInspo = (fieldId, updates) => {
    onDefaultsInspoChange({
      ...defaultsInspo,
      [fieldId]: { ...defaultsInspo[fieldId], ...updates }
    });
  };

  const allQuestions = getIntakeQuestions();

  // Render a single question row
  const renderQuestionRow = (question, sectionId = null) => (
    <div key={question.id} className="flex items-center gap-2 p-3 rounded-lg border bg-white hover:bg-gray-50 transition-colors">
      <GripVertical className="w-4 h-4 text-gray-400 cursor-move flex-shrink-0" />
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm truncate">{question.label}</span>
          {question.isStandard && <Badge variant="secondary" className="text-xs">Standard</Badge>}
          {question.required && <Badge variant="destructive" className="text-xs">Required</Badge>}
        </div>
        <div className="flex items-center gap-2 mt-1">
          <Badge variant="outline" className="text-xs">
            {FIELD_TYPES.find(t => t.id === question.type)?.label || question.type}
          </Badge>
          {question.placeholder && (
            <span className="text-xs text-gray-400 truncate">{question.placeholder}</span>
          )}
        </div>
      </div>
      
      <div className="flex items-center gap-2 flex-shrink-0">
        {/* Enabled toggle */}
        <div className="flex items-center gap-1">
          <span className="text-xs text-gray-500">Show</span>
          <Switch
            checked={question.enabled !== false}
            onCheckedChange={(v) => handleUpdateQuestion(question.id, { enabled: v })}
          />
        </div>
        
        {/* Required toggle */}
        <div className="flex items-center gap-1">
          <span className="text-xs text-gray-500">Req</span>
          <Switch
            checked={question.required === true}
            onCheckedChange={(v) => handleUpdateQuestion(question.id, { required: v })}
            disabled={question.enabled === false}
          />
        </div>
        
        {/* Edit button */}
        <Button 
          variant="ghost" 
          size="icon" 
          className="h-8 w-8"
          onClick={() => setEditingField(question)}
        >
          <Edit2 className="w-4 h-4" />
        </Button>
        
        {/* Delete button (only for custom questions) */}
        {!question.isStandard && (
          <Button 
            variant="ghost" 
            size="icon" 
            className="h-8 w-8 text-red-500 hover:text-red-600"
            onClick={() => handleDeleteQuestion(question.id)}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        )}
      </div>
    </div>
  );

  // Count total questions across all sections
  const getTotalQuestions = () => {
    if (hasSections) {
      return allQuestions.reduce((sum, section) => sum + (section.questions?.length || 0), 0);
    }
    return allQuestions.length;
  };

  return (
    <div className="space-y-6">
      {/* SECTION 1: INTAKE QUESTIONS */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <MessageSquare className="w-4 h-4" />
                Intake Questions ({getTotalQuestions()} total)
              </CardTitle>
              <CardDescription>Questions shown on the RSVP form. Admin can edit, hosts can toggle.</CardDescription>
            </div>
            <Button size="sm" onClick={() => setShowAddDialog(true)}>
              <Plus className="w-4 h-4 mr-1" />
              Add Question
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* If we have sections (from intake_templates.py) */}
          {hasSections ? (
            allQuestions.map((section) => (
              <div key={section.id} className="border rounded-lg overflow-hidden">
                {/* Section Header - Collapsible */}
                <div 
                  className="flex items-center justify-between p-3 bg-gray-50 cursor-pointer hover:bg-gray-100"
                  onClick={() => toggleSection(section.id)}
                >
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm">{section.title}</span>
                    <Badge variant="outline" className="text-xs">{section.questions?.length || 0} questions</Badge>
                    {section.subtitle && (
                      <span className="text-xs text-gray-500">{section.subtitle}</span>
                    )}
                  </div>
                  {expandedSections[section.id] ? (
                    <ChevronUp className="w-4 h-4 text-gray-500" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-gray-500" />
                  )}
                </div>
                
                {/* Section Questions - Collapsible */}
                {(expandedSections[section.id] || expandedSections[section.id] === undefined) && (
                  <div className="p-2 space-y-2">
                    {(section.questions || []).map((question) => renderQuestionRow(question, section.id))}
                    {(!section.questions || section.questions.length === 0) && (
                      <p className="text-sm text-gray-400 text-center py-2">No questions in this section</p>
                    )}
                  </div>
                )}
              </div>
            ))
          ) : (
            /* Flat questions (fallback) */
            <>
              {allQuestions.map((question, index) => renderQuestionRow(question))}
              
              {allQuestions.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No intake questions configured</p>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* SECTION 2: DEFAULTS, INSPO & EXPERIENCE */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Sparkles className="w-4 h-4" />
            Defaults, Inspo & Experience
          </CardTitle>
          <CardDescription>Additional preferences and experience settings (separate from intake questions)</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Dietary Restrictions */}
          <div className="flex items-center justify-between p-3 rounded-lg border bg-gray-50/50">
            <div className="flex items-center gap-3">
              <Utensils className="w-4 h-4 text-orange-500" />
              <div>
                <span className="text-sm font-medium">Dietary Restrictions</span>
                <p className="text-xs text-gray-500">Collect dietary preferences from attendees</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">Show</span>
                <Switch
                  checked={defaultsInspo.dietary?.enabled !== false}
                  onCheckedChange={(v) => updateDefaultsInspo("dietary", { enabled: v })}
                />
              </div>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setEditingStandardField({ id: "dietary", ...defaultsInspo.dietary })}
              >
                <Edit2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
          
          {/* Accessibility Needs */}
          <div className="flex items-center justify-between p-3 rounded-lg border bg-gray-50/50">
            <div className="flex items-center gap-3">
              <Accessibility className="w-4 h-4 text-blue-500" />
              <div>
                <span className="text-sm font-medium">Accessibility Needs</span>
                <p className="text-xs text-gray-500">Collect accessibility requirements</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">Show</span>
                <Switch
                  checked={defaultsInspo.accessibility?.enabled !== false}
                  onCheckedChange={(v) => updateDefaultsInspo("accessibility", { enabled: v })}
                />
              </div>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setEditingStandardField({ id: "accessibility", ...defaultsInspo.accessibility })}
              >
                <Edit2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
          
          {/* Music Preferences */}
          <div className="flex items-center justify-between p-3 rounded-lg border bg-gray-50/50">
            <div className="flex items-center gap-3">
              <Music className="w-4 h-4 text-purple-500" />
              <div>
                <span className="text-sm font-medium">Music Preferences</span>
                <p className="text-xs text-gray-500">Let attendees share their music taste</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">Show</span>
                <Switch
                  checked={defaultsInspo.music?.enabled === true}
                  onCheckedChange={(v) => updateDefaultsInspo("music", { enabled: v })}
                />
              </div>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setEditingStandardField({ id: "music", ...defaultsInspo.music })}
              >
                <Edit2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
          
          {/* Style Inspiration */}
          <div className="flex items-center justify-between p-3 rounded-lg border bg-gray-50/50">
            <div className="flex items-center gap-3">
              <Palette className="w-4 h-4 text-pink-500" />
              <div>
                <span className="text-sm font-medium">Style Inspiration</span>
                <p className="text-xs text-gray-500">Collect style/aesthetic preferences</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">Show</span>
                <Switch
                  checked={defaultsInspo.style?.enabled === true}
                  onCheckedChange={(v) => updateDefaultsInspo("style", { enabled: v })}
                />
              </div>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setEditingStandardField({ id: "style", ...defaultsInspo.style })}
              >
                <Edit2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ADD QUESTION DIALOG */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Intake Question</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Question Label *</Label>
              <Input
                value={newField.label}
                onChange={(e) => setNewField(prev => ({ ...prev, label: e.target.value }))}
                placeholder="e.g., What's your experience level?"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Input Type</Label>
              <Select 
                value={newField.type} 
                onValueChange={(v) => setNewField(prev => ({ ...prev, type: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FIELD_TYPES.map(type => (
                    <SelectItem key={type.id} value={type.id}>
                      <div className="flex items-center gap-2">
                        <type.icon className="w-4 h-4" />
                        <span>{type.label}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {(newField.type === "select" || newField.type === "multiselect") && (
              <div className="space-y-2">
                <Label>Options (one per line)</Label>
                <Textarea
                  value={newField.options?.join("\n") || ""}
                  onChange={(e) => setNewField(prev => ({ 
                    ...prev, 
                    options: e.target.value.split("\n").filter(o => o.trim()) 
                  }))}
                  placeholder="Option 1&#10;Option 2&#10;Option 3"
                  rows={4}
                />
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Placeholder Text</Label>
              <Input
                value={newField.placeholder}
                onChange={(e) => setNewField(prev => ({ ...prev, placeholder: e.target.value }))}
                placeholder="Enter placeholder text..."
              />
            </div>
            
            <div className="space-y-2">
              <Label>Help Text</Label>
              <Input
                value={newField.helpText}
                onChange={(e) => setNewField(prev => ({ ...prev, helpText: e.target.value }))}
                placeholder="Additional guidance for this field"
              />
            </div>
            
            <div className="flex items-center justify-between p-3 rounded-lg border">
              <Label>Required Field</Label>
              <Switch
                checked={newField.required}
                onCheckedChange={(v) => setNewField(prev => ({ ...prev, required: v }))}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button onClick={handleAddQuestion} disabled={!newField.label.trim()}>Add Question</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* EDIT QUESTION DIALOG */}
      <Dialog open={!!editingField} onOpenChange={() => setEditingField(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Question</DialogTitle>
          </DialogHeader>
          
          {editingField && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Question Label</Label>
                <Input
                  value={editingField.label}
                  onChange={(e) => setEditingField(prev => ({ ...prev, label: e.target.value }))}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Input Type</Label>
                <Select 
                  value={editingField.type} 
                  onValueChange={(v) => setEditingField(prev => ({ ...prev, type: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FIELD_TYPES.map(type => (
                      <SelectItem key={type.id} value={type.id}>
                        <div className="flex items-center gap-2">
                          <type.icon className="w-4 h-4" />
                          <span>{type.label}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {(editingField.type === "select" || editingField.type === "multiselect") && (
                <div className="space-y-2">
                  <Label>Options (one per line)</Label>
                  <Textarea
                    value={editingField.options?.join("\n") || ""}
                    onChange={(e) => setEditingField(prev => ({ 
                      ...prev, 
                      options: e.target.value.split("\n").filter(o => o.trim()) 
                    }))}
                    rows={4}
                  />
                </div>
              )}
              
              <div className="space-y-2">
                <Label>Placeholder Text</Label>
                <Input
                  value={editingField.placeholder || ""}
                  onChange={(e) => setEditingField(prev => ({ ...prev, placeholder: e.target.value }))}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Help Text</Label>
                <Input
                  value={editingField.helpText || ""}
                  onChange={(e) => setEditingField(prev => ({ ...prev, helpText: e.target.value }))}
                />
              </div>
              
              <div className="flex items-center justify-between p-3 rounded-lg border">
                <Label>Required Field</Label>
                <Switch
                  checked={editingField.required}
                  onCheckedChange={(v) => setEditingField(prev => ({ ...prev, required: v }))}
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingField(null)}>Cancel</Button>
            <Button onClick={() => {
              if (editingField) {
                handleUpdateQuestion(editingField.id, editingField);
                setEditingField(null);
              }
            }}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
